import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FolderIcon, PlusIcon, TrashIcon, PencilIcon, FolderOpenIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [collectionsHasMore, setCollectionsHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items, hasMore } = await bookmarkService.listCollections();
      setCollections(items);
      setCollectionsHasMore(hasMore);
    } catch (err) {
      setError(err.message || 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (collectionId) => {
    if (!window.confirm('Delete this collection? The bookmarks inside will not be deleted.')) return;
    try {
      await bookmarkService.deleteCollection(collectionId);
      setCollections(prev => prev.filter(c => c.collectionId !== collectionId));
    } catch (err) {
      alert(err.message || 'Failed to delete collection');
    }
  };

  const handleSaved = (saved) => {
    setCollections(prev => {
      const idx = prev.findIndex(c => c.collectionId === saved.collectionId);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = saved;
        return updated;
      }
      return [saved, ...prev];
    });
    setShowCreateModal(false);
    setEditingCollection(null);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Collections</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Organise your bookmarks into collections</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="h-4 w-4" />
          <span>New Collection</span>
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {collectionsHasMore && (
        <div className="mb-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-sm rounded-lg px-4 py-2.5">
          Showing the 200 most recently created collections.
        </div>
      )}

      {!loading && !error && collections.length === 0 && (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <FolderOpenIcon className="h-16 w-16 mx-auto mb-4 opacity-40" />
          <p className="text-lg font-medium">No collections yet</p>
          <p className="text-sm mt-1">Create a collection to group related bookmarks together</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary mt-4 inline-flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Create your first collection</span>
          </button>
        </div>
      )}

      {!loading && collections.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map(c => (
            <CollectionCard
              key={c.collectionId}
              collection={c}
              onDelete={handleDelete}
              onEdit={() => setEditingCollection(c)}
            />
          ))}
        </div>
      )}

      {(showCreateModal || editingCollection) && (
        <CollectionFormModal
          collection={editingCollection}
          onClose={() => { setShowCreateModal(false); setEditingCollection(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

const CollectionCard = ({ collection, onDelete, onEdit }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow p-5">
    <div className="flex items-start justify-between gap-2 mb-3">
      <Link
        to={`/collections/${collection.collectionId}`}
        className="flex items-center gap-2 min-w-0 group"
      >
        <FolderIcon className="h-6 w-6 text-primary-500 flex-shrink-0 group-hover:text-primary-700 transition-colors" />
        <span className="font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">
          {collection.name}
        </span>
        {collection.isShared && (
          <span title="Publicly shared" className="flex-shrink-0">
            <GlobeAltIcon className="h-3.5 w-3.5 text-green-500" />
          </span>
        )}
      </Link>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Rename"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(collection.collectionId)}
          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
          title="Delete"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>

    {collection.description && (
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">{collection.description}</p>
    )}

    <div className="flex items-center justify-between mt-auto">
      <span className="text-xs text-gray-400 dark:text-gray-500">
        {collection.bookmarkCount === 1 ? '1 bookmark' : `${collection.bookmarkCount || 0} bookmarks`}
      </span>
      <Link
        to={`/collections/${collection.collectionId}`}
        className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium transition-colors"
      >
        Open →
      </Link>
    </div>
  </div>
);

const CollectionFormModal = ({ collection, onClose, onSaved }) => {
  const [name, setName] = useState(collection?.name || '');
  const [description, setDescription] = useState(collection?.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      let saved;
      if (collection) {
        saved = await bookmarkService.updateCollection(collection.collectionId, { name: trimmed, description: description.trim() });
        // updateCollection may return the updated object or nothing depending on impl; merge locally
        saved = saved || { ...collection, name: trimmed, description: description.trim() };
      } else {
        saved = await bookmarkService.createCollection(trimmed, description.trim());
      }
      onSaved(saved);
    } catch (err) {
      setError(err.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {collection ? 'Edit Collection' : 'New Collection'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g. Web Dev Resources"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Optional description"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary text-sm" disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary text-sm" disabled={saving}>
              {saving ? 'Saving…' : collection ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CollectionsPage;
